import { useState } from "react";
import { useNavigate } from "react-router-dom";
import AppLayout from "./AppLayout";
import { Button } from "./ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "./ui/card";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";
import { Textarea } from "./ui/textarea";
import { ArrowLeft, Calendar, CheckCircle } from "lucide-react";
import authService from "../Services/authService";

const LeaveApplicationScreen = () => {
  const navigate = useNavigate();
  const [leaveType, setLeaveType] = useState("");
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");
  const [reason, setReason] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState("");

  const validateForm = () => {
    if (!fromDate || !toDate || !reason.trim()) {
      setError("All fields are required.");
      return false;
    }
    if (new Date(fromDate) > new Date(toDate)) {
      setError("From date cannot be after To date.");
      return false;
    }
    return true;
  };

const handleSubmit = async (e) => {
  e.preventDefault();

  // Simple validation check
  if (!fromDate || !toDate || !reason.trim()) {
    alert("Please fill all required fields.");
    return;
  }

  setIsSubmitting(true);

  const token = authService.getCurrentUsertoken();
  const url = new URL(authService.getApiUrl() + "applyleave");

  try {
    const response = await fetch(url, {
      method: "POST",
      body: JSON.stringify({
        user_id: authService.getCurrentuser().id,
        from_date: fromDate,
        to_date: toDate,
        remarks: reason,
        status: "Leave", // if needed
      }),
      headers: {
        Accept: "application/json",
        "Content-Type": "application/json; charset=UTF-8",
        Authorization: `Bearer ${token}`,
      },
    });

    if (response.status === 401) {
      authService.clientlogout();
      window.location.href = "/login";
      return;
    }

    const result = await response.json();

    if (result) {
      setSubmitted(true); // ✅ show success UI
    } else {
      alert(result.message || "Something went wrong.");
    }
  } catch (error) {
    console.error("Error submitting leave:", error);
    alert("Failed to submit leave. Please try again.");
  } finally {
    setIsSubmitting(false);
  }
};


  return (
    <AppLayout>
      <div className="flex items-center mb-6">
        <Button
          variant="ghost"
          size="icon"
          onClick={() => navigate(-1)}
          className="mr-2 text-gray-500 hover:text-gray-900 hover:bg-gray-100"
        >
          <ArrowLeft className="h-5 w-5" />
        </Button>
        <div className="flex items-center">
          <div className="relative h-[100px] w-[100px] mr-3">
            <img src="/images/pranaam-365-logo.png" alt="Pranaam365 Logo" className="w-full h-full object-contain" />
          </div>
          <h1 className="text-xl font-bold text-gray-800">Leave Application</h1>
        </div>
      </div>

      {submitted ? (
        <Card className="border-gray-200">
          <CardContent className="pt-6 pb-6 text-center">
            <div className="flex flex-col items-center">
              <div className="rounded-full bg-green-100 p-3 mb-4">
                <CheckCircle className="h-8 w-8 text-green-600" />
              </div>
              <h2 className="text-xl font-bold mb-2 text-gray-800">Leave Applied Successfully</h2>
              <p className="text-gray-500 mb-6">Your leave application has been submitted and is pending approval.</p>
              <Button
                onClick={() => navigate("/attendance")}
                className="w-full bg-pranaam-red hover:bg-red-700 text-white"
              >
                Back to Attendance
              </Button>
            </div>
          </CardContent>
        </Card>
      ) : (
        <Card className="border-gray-200">
          <CardHeader className="pb-2">
            <CardTitle className="text-lg text-gray-800">Apply for Leave</CardTitle>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-4">
              {/* Leave Type */}
              {/* <div className="space-y-2">
                <Label htmlFor="leave-type">Leave Type</Label>
                <Select value={leaveType} onValueChange={setLeaveType}>
                  <SelectTrigger id="leave-type">
                    <SelectValue placeholder="Select leave type" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="casual">Casual Leave</SelectItem>
                    <SelectItem value="sick">Sick Leave</SelectItem>
                    <SelectItem value="earned">Earned Leave</SelectItem>
                    <SelectItem value="unpaid">Unpaid Leave</SelectItem>
                  </SelectContent>
                </Select>
              </div> */}

              {/* Dates */}
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2 text-left">
                  <Label htmlFor="from-date">From Date</Label>
                  <div className="relative">
                    <Calendar className="absolute left-3 top-3 h-4 w-4 text-gray-400" />
                    <Input
                      id="from-date"
                      type="date"
                      value={fromDate}
                      onChange={(e) => setFromDate(e.target.value)}
                      className="pl-10"
                      required
                    />
                  </div>
                </div>

                <div className="space-y-2 text-left">
                  <Label htmlFor="to-date">To Date</Label>
                  <div className="relative">
                    <Calendar className="absolute left-3 top-3 h-4 w-4 text-gray-400" />
                    <Input
                      id="to-date"
                      type="date"
                      value={toDate}
                      onChange={(e) => setToDate(e.target.value)}
                      className="pl-10"
                      required
                    />
                  </div>
                </div>
              </div>

              {/* Reason */}
              <div className="space-y-2 text-left">
                <Label htmlFor="reason">Reason for Leave</Label>
                <Textarea
                  id="reason"
                  placeholder="Please provide a reason for your leave request..."
                  value={reason}
                  onChange={(e) => setReason(e.target.value)}
                  className="min-h-[100px]"
                  required
                />
              </div>

              {/* Error */}
              {error && <p className="text-sm text-red-600">{error}</p>}

              {/* Submit */}
              <Button
                type="submit"
                className="w-full bg-pranaam-red hover:bg-red-700 text-white"
                disabled={isSubmitting}
              >
                {isSubmitting ? "Submitting..." : "Submit Leave Application"}
              </Button>
            </form>
          </CardContent>
        </Card>
      )}
    </AppLayout>
  );
};

export default LeaveApplicationScreen;
